import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, TableCell, TableHead, TableRow, TextField, Tooltip } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import FilterListIcon from '@material-ui/icons/FilterList';

const FileTableHeader = ({ sortConfig, handleSort, className, loading, handleFilter }) => {
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});

  const disabledStyle = useMemo(() => (loading ? { pointerEvents: 'none', opacity: 0.5 } : {}), [loading]);

  const handleIconClick = (key) => {
    if (!loading) {
      setSelectedIcon((prev) => (prev === key ? null : key));
    }
  };

  const handleFilterNew = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      if (value.trim() === '') {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [name]: value };
    });
  };

  const getSortIcon = (key) => {
    const isSelected = selectedIcon === key;
    const iconStyle = { color: isSelected ? 'blue' : 'inherit' };

    return (
      <IconButton onClick={() => handleIconClick(key)} style={iconStyle} aria-label={`Sort by ${key}`}
                  disabled={loading}>
        {sortConfig.key === key && sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> :
          <ArrowDownwardIcon fontSize="small" />}
      </IconButton>
    );
  };

  const toggleFilter = () => {
    if (!loading) {
      setShowFilter((prev) => !prev);
    }
  };

  const applyFilter = () => {
    handleFilter(filters);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell className={className} style={disabledStyle}>
          <Tooltip title="Filter by File Name">
            <IconButton
              size="small"
              onClick={toggleFilter}
              aria-label="Toggle filter by File Name"
              style={{ color: showFilter ? 'blue' : 'inherit' }}
              disabled={loading}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <span onClick={() => !loading && handleSort('file_name')}>File Name {getSortIcon('file_name')}</span>
        </TableCell>
        <TableCell className={className} onClick={() => !loading && handleSort('uploaded_by')}>
          Uploaded By {getSortIcon('uploaded_by')}
        </TableCell>
        <TableCell className={className} onClick={() => !loading && handleSort('uploaded_at')}>
          Uploaded Date {getSortIcon('uploaded_at')}
        </TableCell>
        <TableCell className={className} onClick={() => !loading && handleSort('file_content')}>
          File Size {getSortIcon('file_content')}
        </TableCell>
        <TableCell className={className}>URL</TableCell>
        <TableCell className={className}>Delete</TableCell>
      </TableRow>
      {showFilter && (
        <TableRow>
          <TableCell className={className} style={disabledStyle}>
            <Box>
              <TextField
                name="file_name"
                value={filters.file_name || ''}
                onChange={handleFilterNew}
                placeholder="Filter by File Name"
                size="small"
                variant="outlined"
                disabled={loading}
                onBlur={applyFilter}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applyFilter();
                  }
                }}
                aria-label="Filter by File Name"
              />
            </Box>
          </TableCell>
          <TableCell className={className} />
          <TableCell className={className} />
          <TableCell className={className} />
          <TableCell className={className} />
          <TableCell className={className} />
        </TableRow>
      )}
    </TableHead>
  );
};

FileTableHeader.propTypes = {
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
  handleSort: PropTypes.func.isRequired,
  className: PropTypes.string,
  loading: PropTypes.bool,
  handleFilter: PropTypes.func.isRequired,
};

FileTableHeader.defaultProps = {
  className: '',
  loading: false,
};

export default FileTableHeader;
